const STORAGE_KEY = 'lastGeneratedDocumentIds';

export type DocumentIdCacheDomain = 'documents' | 'purchases' | 'tickets';

interface CachedEntry {
    id: number;
    id_by_company?: number | null;
    savedAt: number;
}

interface CacheShape {
    documents?: Partial<Record<string, CachedEntry>>;
    purchases?: Partial<Record<string, CachedEntry>>;
    tickets?: CachedEntry;
}

/**
 * Records, per document type, the id of the last document/ticket/purchase saved
 * in this tab - purely so that if the power goes out right after saving, whoever
 * is at the machine can still find the id needed to reprint from
 * sessionStorage's devtools (`lastGeneratedDocumentIds`) and open the existing
 * print route (/invoice/print/:id, /purchase-invoice/print/:id,
 * /ticket/print-gift/:id) directly.
 *
 * Deliberately a narrow static helper, not a generic storage-wrapper service -
 * no such wrapper exists anywhere else in this codebase (every other
 * localStorage/sessionStorage usage is a direct call), and this is the only
 * thing it needs to do. Written on every successful save (online or
 * offline-queued alike), not just offline ones - a power outage can strike
 * moments after an online save too.
 *
 * Caveat: sessionStorage is per-tab and is cleared on tab/browser close, so it
 * does NOT survive an actual power-off/reboot, only in-tab reloads/navigation.
 */
export class DocumentIdCache {

    static record(domain: 'documents' | 'purchases', type: string, id: number, idByCompany?: number | null): void;
    static record(domain: 'tickets', type: null, id: number, idByCompany?: number | null): void;
    static record(domain: DocumentIdCacheDomain, type: string | null, id: number, idByCompany: number | null = null): void {
        const cache = DocumentIdCache.getAll();
        const entry: CachedEntry = { id, id_by_company: idByCompany, savedAt: Date.now() };

        if (domain === 'tickets') {
            cache.tickets = entry;
        } else {
            cache[domain] = { ...cache[domain], [type as string]: entry };
        }

        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
        } catch {
            // Ignore (private-browsing storage restrictions, quota, etc.) - this is a best-effort recovery aid, not critical state.
        }
    }

    static getAll(): CacheShape {
        try {
            const stored = sessionStorage.getItem(STORAGE_KEY);
            return stored ? JSON.parse(stored) : {};
        } catch {
            return {};
        }
    }
}
